import React from 'react';
import { useStore } from '../state/store';
import { OperationCard } from '../components/OperationCard';
import { Card, CardBody } from '../design/primitives';

/**
 * Shared layout for the four banking areas.
 *
 * Records first, operations second. That ordering matters: an operator looks at
 * a customer's account and then acts on it, so a page that leads with a bare
 * list of tool names reads like an API console rather than a bank.
 */
export function BankArea({
  lede,
  serverName,
  children,
}: {
  lede: string;
  serverName: string | string[];
  children?: React.ReactNode;
}) {
  const { bootstrap } = useStore();
  if (!bootstrap) return null;

  const servers = Array.isArray(serverName) ? serverName : [serverName];
  const tools = bootstrap.estate.tools.filter((tool: any) => servers.includes(tool.serverName));
  const serverRecords = bootstrap.estate.mcpServers.filter((server: any) => servers.includes(server.name));

  return (
    <div className="mb-page">
      <Card>
        <CardBody>
          <p className="mb-lede">{lede}</p>
          <div className="mb-server-strip">
            {serverRecords.map((server: any) => (
              <div key={server.name} className="mb-server-chip">
                <code>{server.name}</code>
                <span>{server.owner}</span>
                <span data-proxied={String(server.proxied)}>
                  {server.proxied ? 'behind the FCD gateway' : 'direct — not proxied'}
                </span>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      {children}

      <section>
        <h2 className="mb-section-title">Operations</h2>
        <p className="mb-section-sub">
          Every operation here is a real MCP tool call. It is checked before it runs, and the full decision path appears
          on the right.
        </p>
        <div className="mb-op-grid">
          {tools.map((tool: any) => (
            <OperationCard key={tool.name} tool={tool} />
          ))}
        </div>
      </section>
    </div>
  );
}

/** A compact records table for the demo's fictional data. */
export function RecordTable({
  title,
  note,
  columns,
  rows,
}: {
  title: string;
  note?: string;
  columns: Array<{ key: string; label: string; align?: 'right' }>;
  rows: Array<Record<string, React.ReactNode>>;
}) {
  return (
    <section>
      <h2 className="mb-section-title">{title}</h2>
      {note && <p className="mb-section-sub">{note}</p>}
      <Card>
        <CardBody flush>
          <table className="mb-table">
            <caption className="mb-sr">{title}</caption>
            <thead>
              <tr>
                {columns.map(column => (
                  <th key={column.key} scope="col" className={column.align === 'right' ? 'mb-right' : undefined}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  {columns.map(column => (
                    <td key={column.key} className={column.align === 'right' ? 'mb-right' : undefined}>
                      {row[column.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </section>
  );
}
